import { useEffect, useState } from "react";
import { BrandIcNotUploaded } from "@racoon/icons";
import { Namespace, useT } from "@racoon/i18n";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  cn,
  type CarouselApi,
} from "@racoon/ui";
import type { JobPhotoGroup, JobPhotoSlide } from "~/shared/utils/jobPhotos";
import { formatPhotoUploadedAt } from "../lib/job-formatters";

const GROUP_LABEL_KEYS = {
  before: "wasteExport.photoBefore",
  after: "wasteExport.photoAfter",
  failure: "wasteExport.noExportPhoto",
} as const satisfies Record<JobPhotoGroup, string>;

const CHIP = "rounded-md bg-slate-900/80 px-2 py-1 text-xs font-medium leading-4 text-white";

/**
 * Every photo of one job in a single carousel, shown whole (object-contain on
 * a dark backdrop, so portrait shots are not cropped), stamped with its group
 * and upload time. Remount it per job to start each report from its first photo.
 */
export const JobPhotoCarousel = ({
  slides,
  onApiChange,
}: {
  slides: JobPhotoSlide[];
  /**
   * Hands the live carousel instance up, so the viewer can page it from the
   * keyboard — and undefined once it is gone, so a remount never leaves the
   * viewer holding a destroyed one.
   */
  onApiChange: (api: CarouselApi | undefined) => void;
}) => {
  const t = useT();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    onApiChange(api);
    return () => onApiChange(undefined);
  }, [api, onApiChange]);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  if (slides.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 rounded-lg bg-slate-100">
        <BrandIcNotUploaded width={48} height={48} />
        <p className="text-sm text-text-secondary">
          {t(Namespace.Govdash, "wasteExport.noPhotos")}
        </p>
      </div>
    );
  }

  const slide = slides[Math.min(current, slides.length - 1)];

  return (
    <Carousel
      setApi={setApi}
      className="h-full overflow-hidden rounded-lg bg-slate-900 [&_[data-slot=carousel-content]]:h-full"
    >
      <CarouselContent className="!ml-0 h-full">
        {slides.map(({ url, group }, index) => (
          <CarouselItem key={`${url}-${index}`} className="h-full !pl-0">
            <img
              src={url}
              alt={`${t(Namespace.Govdash, GROUP_LABEL_KEYS[group])} ${index + 1}`}
              className="h-full w-full object-contain"
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      <span className={cn(CHIP, "absolute left-3 top-3")}>
        {t(Namespace.Govdash, GROUP_LABEL_KEYS[slide.group])}
      </span>
      {slide.uploadedAt && (
        <span className={cn(CHIP, "absolute right-3 top-3")}>
          {formatPhotoUploadedAt(slide.uploadedAt)}
        </span>
      )}

      {slides.length > 1 && (
        <>
          <CarouselPrevious className="left-3 border-none bg-white/90 hover:bg-white" />
          <CarouselNext className="right-3 border-none bg-white/90 hover:bg-white" />
          <div className="absolute bottom-3 left-0 flex w-full justify-center gap-2">
            {slides.map(({ url }, index) => (
              <button
                key={`dot-${url}-${index}`}
                type="button"
                aria-label={`${index + 1} / ${slides.length}`}
                aria-current={index === current}
                onClick={() => api?.scrollTo(index)}
                className={cn(
                  "size-2 rounded-full transition-colors",
                  index === current ? "bg-white" : "bg-white/40 hover:bg-white/70",
                )}
              />
            ))}
          </div>
        </>
      )}
    </Carousel>
  );
};
